import { useEffect, useRef, useState } from "react";
import {
  clampDormX,
  dormBubbles,
  dormNativeWidth,
  dormPieces,
  dormRoom,
  dormSitSpots,
  dormXBounds,
  nearestSit,
} from "../../data/dorm";
import { costumeLabels, type CostumeId } from "../../data/gallery";
import { dormPacks, pickClip, spineConfig } from "../../data/spineConfig";
import { useMediaQuery, usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import {
  loadGflSpineData,
  loadPngImage,
  loadSpineRuntime,
  playClip,
  type PixiApp,
  type PixiDisplay,
  type PixiRuntime,
  type SpineSprite,
} from "../../lib/spine";
import { SectionShell } from "../layout/SectionShell";
import styles from "./DormViewer.module.css";

type DollState = {
  x: number;
  facing: 1 | -1;
  walking: boolean;
  sitting: boolean;
  dragging: boolean;
  falling: boolean;
  fallFrom: number;
  fallT: number;
  dragY: number;
  sitDy: number;
  clip: string;
};

const BACK_LAYERS: Array<(typeof dormPieces)[number]["layer"]> = [
  "wall",
  "floor",
  "carpet",
  "back",
  "mid",
];

async function spriteFrom(
  pixi: PixiRuntime,
  src: string,
): Promise<{ spr: PixiDisplay; w: number; h: number } | null> {
  try {
    const img = await loadPngImage(src);
    const base = new pixi.BaseTexture(img) as { scaleMode?: number };
    const linear = (pixi as PixiRuntime & { SCALE_MODES?: { LINEAR: number } }).SCALE_MODES?.LINEAR;
    if (linear !== undefined) base.scaleMode = linear;
    const spr = new pixi.Sprite(new pixi.Texture(base));
    return { spr, w: img.width, h: img.height };
  } catch (err) {
    console.warn("Dorm sprite failed", src, err);
    return null;
  }
}

function hitChibi(chibi: SpineSprite | null, lx: number, ly: number, fallback: { x: number; y: number; scale: number }) {
  if (!chibi) return false;
  const b = (chibi as SpineSprite & { getBounds?: () => { x: number; y: number; width: number; height: number } }).getBounds?.();
  if (b && b.width > 8 && b.height > 8) {
    const pad = 10;
    return lx >= b.x - pad && lx <= b.x + b.width + pad && ly >= b.y - pad && ly <= b.y + b.height + pad;
  }
  return Math.abs(lx - fallback.x) < 56 && ly < fallback.y + 10 && ly > fallback.y - 160 * fallback.scale;
}

export function DormViewer() {
  const host = useRef<HTMLDivElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const bubbleEl = useRef<HTMLDivElement>(null);
  const doll = useRef<DollState>({
    x: 900,
    facing: 1,
    walking: false,
    sitting: false,
    dragging: false,
    falling: false,
    fallFrom: 0,
    fallT: 0,
    dragY: 0,
    sitDy: 0,
    clip: "wait",
  });
  const sprite = useRef<SpineSprite | null>(null);
  const playNamed = useRef<(name: string, loop: boolean) => void>(() => {});
  const wanderRef = useRef(true);
  const [packId, setPackId] = useState<CostumeId>("default");
  const [viewW, setViewW] = useState(0);
  const [wander, setWander] = useState(true);
  const [failed, setFailed] = useState<string | null>(null);
  const [bubble, setBubble] = useState<string | null>(null);
  wanderRef.current = wander;
  const reduced = usePrefersReducedMotion();
  const mobile = useMediaQuery("(max-width: 720px)");
  const nativeW = dormNativeWidth();
  const scale = (viewW > 200 ? viewW : 1120) / nativeW;
  const roomW = Math.round(nativeW * scale);
  const roomH = Math.round(dormRoom.wallSize * scale);
  const groundY = roomH - dormRoom.groundPad;
  const chibiScale = mobile ? 0.5 : 0.68;
  const layoutKey = dormPieces.map((p) => `${p.id}:${p.x}:${p.y ?? 0}:${p.scale ?? 1}`).join("|");

  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const apply = () => {
      const w = el.clientWidth;
      if (w >= 280) setViewW((prev) => (Math.abs(prev - w) < 4 ? prev : w));
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = host.current;
    if (!el || viewW < 280) return;
    let destroyed = false;
    let app: PixiApp | null = null;
    let bubbleTimer = 0;
    let startTimer = 0;
    const cleanups: Array<() => void> = [];

    const abort = () => destroyed;

    async function boot() {
      if (abort() || !el) return;
      try {
        const pixi = await loadSpineRuntime();
        if (abort() || !el) return;
        app = new pixi.Application({
          width: roomW,
          height: roomH,
          transparent: true,
          antialias: true,
        });
        el.replaceChildren(app.view);
        app.view.style.display = "block";
        app.view.style.touchAction = "none";

        const byLayer = new Map<string, PixiDisplay[]>();
        for (const layer of [...BACK_LAYERS, "front"]) byLayer.set(layer, []);

        const wall = await spriteFrom(pixi, dormRoom.wallSrc);
        if (abort()) return;
        if (!wall) {
          setFailed("Library wall sprite failed to decode.");
          return;
        }
        if (wall && app) {
          for (let i = 0; i < dormRoom.tiles; i++) {
            const tile = i === 0 ? wall.spr : new pixi.Sprite(wall.spr.texture);
            tile.anchor?.set(0, 0);
            tile.scale.set(scale);
            tile.x = i * dormRoom.wallSize * scale;
            tile.y = 0;
            byLayer.get("wall")?.push(tile);
          }
        }

        const floor = await spriteFrom(pixi, dormRoom.floorSrc);
        if (floor && app) {
          const tileW = dormRoom.floorSize * scale;
          const count = Math.ceil(roomW / tileW) + 1;
          for (let i = 0; i < count; i++) {
            const tile = i === 0 ? floor.spr : new pixi.Sprite(floor.spr.texture);
            tile.anchor?.set(0, 1);
            tile.scale.set(scale);
            tile.x = i * tileW;
            tile.y = roomH;
            byLayer.get("floor")?.push(tile);
          }
        }

        await Promise.all(
          dormPieces.map(async (piece) => {
            const loaded = await spriteFrom(pixi, piece.src);
            if (!loaded || abort()) return;
            const [ax, ay] = piece.anchor ?? [0.5, 1];
            loaded.spr.anchor?.set(ax, ay);
            const sx = scale * (piece.scale ?? 1);
            const sy = sx * (piece.scaleY ?? 1);
            loaded.spr.scale.set(sx, sy);
            loaded.spr.x = piece.x * scale;
            loaded.spr.y = groundY + (piece.y ?? 0) * scale;
            if (piece.blendAdd && pixi.BLEND_MODES) {
              loaded.spr.blendMode = pixi.BLEND_MODES.ADD;
            }
            byLayer.get(piece.layer)?.push(loaded.spr);
          }),
        );

        if (!app || abort()) return;
        for (const layer of BACK_LAYERS) {
          for (const child of byLayer.get(layer) ?? []) {
            app.stage.addChild(child);
          }
        }

        const pack = dormPacks.find((p) => p.id === packId) ?? dormPacks[0];
        if (!pack) throw new Error("No dorm spine pack");
        let chibi: SpineSprite | null = null;
        let idle = "wait";
        let move = "move";
        let sit = "sit";
        let pick = "pick";
        try {
          const loaded = await loadGflSpineData(pixi, pack);
          if (abort() || !app) return;
          chibi = new pixi.spine.Spine(loaded.spineData);
          idle = pickClip(loaded.animations, spineConfig.animations.idle) ?? loaded.animations[0] ?? "wait";
          move = pickClip(loaded.animations, spineConfig.animations.move) ?? idle;
          sit = pickClip(loaded.animations, spineConfig.animations.sit) ?? idle;
          pick = pickClip(loaded.animations, spineConfig.animations.pick) ?? idle;
          chibi.y = groundY;
          chibi.x = doll.current.x * scale;
          chibi.scale.set(chibiScale);
          playClip(chibi, idle, true);
          app.stage.addChild(chibi);
          sprite.current = chibi;
          doll.current.clip = idle;
          playNamed.current = (name, loop) => {
            if (!sprite.current) return;
            playClip(sprite.current, name, loop);
            doll.current.clip = name;
          };
        } catch (err) {
          console.warn("Dorm chibi Spine failed", err);
        }

        for (const child of byLayer.get("front") ?? []) {
          app.stage.addChild(child);
        }

        const sits = dormSitSpots();
        const bounds = dormXBounds();
        let target = clampDormX(doll.current.x);
        let pressOnRoom = false;
        let idleUntil = performance.now() + 2400;
        let lastWalk = false;
        let lastSit = false;
        let lastFx = performance.now();

        const dropAt = (nativeX: number, screenY: number) => {
          const d = doll.current;
          d.dragging = false;
          d.x = clampDormX(nativeX);
          const near = nearestSit(d.x, 24);
          if (near) {
            d.x = near.x;
            d.sitting = true;
            d.walking = false;
            d.falling = false;
            d.sitDy = near.y;
            if (near.facing) d.facing = near.facing;
            playNamed.current(sit, true);
            idleUntil = performance.now() + 3200 + Math.random() * 4200;
            return;
          }
          d.sitting = false;
          d.sitDy = 0;
          d.walking = false;
          d.falling = true;
          d.fallFrom = screenY;
          d.fallT = 0;
          playNamed.current(idle, true);
          idleUntil = performance.now() + 900 + Math.random() * 1600;
        };

        app.ticker.add(() => {
          const now = performance.now();
          const dt = Math.min(0.05, (now - lastFx) / 1000);
          lastFx = now;
          const d = doll.current;

          if (d.dragging) {
            d.walking = false;
            d.sitting = false;
            d.falling = false;
          } else if (d.falling) {
            d.fallT += dt / 0.38;
            if (d.fallT >= 1) {
              d.falling = false;
              d.fallT = 1;
            }
          } else if (!reduced) {
            if (wanderRef.current) {
              if (d.sitting && now > idleUntil) d.sitting = false;
              if (now > idleUntil && !d.walking) {
                const nextSit = Math.random() < 0.42 ? sits[Math.floor(Math.random() * sits.length)] : null;
                if (nextSit) {
                  target = nextSit.x;
                } else {
                  target = bounds.min + Math.random() * (bounds.max - bounds.min);
                }
                d.sitting = false;
                d.sitDy = 0;
                d.walking = true;
                d.facing = target >= d.x ? 1 : -1;
              }
            }
            if (d.walking) {
              const speed = (mobile ? 46 : 62) / scale;
              const dir = target >= d.x ? 1 : -1;
              d.facing = dir;
              d.x += dir * speed * dt;
              if ((dir > 0 && d.x >= target) || (dir < 0 && d.x <= target)) {
                d.x = clampDormX(target);
                d.walking = false;
                lastWalk = false;
                const near = nearestSit(d.x);
                d.sitting = Boolean(near);
                if (near) {
                  d.x = near.x;
                  d.sitDy = near.y;
                  if (near.facing) d.facing = near.facing;
                } else {
                  d.sitDy = 0;
                }
                idleUntil = now + 2800 + Math.random() * 5200;
                playNamed.current(near ? sit : idle, true);
              }
            }
          } else {
            d.walking = false;
          }

          d.x = clampDormX(d.x);

          if (chibi) {
            chibi.x = d.x * scale;
            if (d.dragging) {
              chibi.y = d.dragY;
            } else if (d.falling) {
              const t = Math.min(1, d.fallT);
              const ease = t * t;
              chibi.y = d.fallFrom + (groundY - d.fallFrom) * ease;
            } else {
              chibi.y = groundY + (d.sitting ? d.sitDy * scale : 0);
            }
            chibi.scale.x = chibiScale * d.facing;
            chibi.scale.y = chibiScale;
            if (!d.dragging && !d.falling && (d.walking !== lastWalk || d.sitting !== lastSit)) {
              lastWalk = d.walking;
              lastSit = d.sitting;
              if (d.walking) playNamed.current(move, true);
              else playNamed.current(d.sitting ? sit : idle, true);
            }
          }

          if (bubbleEl.current) {
            bubbleEl.current.style.left = `${d.x * scale}px`;
            bubbleEl.current.style.top = `${(chibi?.y ?? groundY) - 118 * chibiScale}px`;
          }
        });

        const localXY = (ev: PointerEvent) => {
          if (!app) return { x: 0, y: 0, nativeX: 0 };
          const rect = app.view.getBoundingClientRect();
          const x = ((ev.clientX - rect.left) * (roomW / rect.width));
          const y = ((ev.clientY - rect.top) * (roomH / rect.height));
          return { x, y, nativeX: x / scale };
        };

        const onPointerDown = (ev: PointerEvent) => {
          if (!app || ev.button !== 0) return;
          const { x, y, nativeX } = localXY(ev);
          const d = doll.current;
          const feetY = groundY + (d.sitting ? d.sitDy * scale : 0);
          if (hitChibi(chibi, x, y, { x: d.x * scale, y: feetY, scale: chibiScale })) {
            ev.preventDefault();
            pressOnRoom = false;
            app.view.setPointerCapture?.(ev.pointerId);
            d.dragging = true;
            d.falling = false;
            d.sitting = false;
            d.walking = false;
            d.sitDy = 0;
            d.x = clampDormX(nativeX);
            d.dragY = y;
            playNamed.current(pick, true);
            app.view.style.cursor = "grabbing";
          } else {
            pressOnRoom = true;
          }
        };

        const onPointerMove = (ev: PointerEvent) => {
          const d = doll.current;
          if (!d.dragging) return;
          const { y, nativeX } = localXY(ev);
          d.x = clampDormX(nativeX);
          d.dragY = Math.max(40, Math.min(groundY + 8, y));
        };

        const onPointerUp = (ev: PointerEvent) => {
          if (!app) return;
          const { y, nativeX } = localXY(ev);
          const d = doll.current;
          if (d.dragging) {
            dropAt(nativeX, y);
            app.view.style.cursor = "pointer";
            pressOnRoom = false;
            return;
          }
          if (!pressOnRoom || ev.button !== 0) {
            pressOnRoom = false;
            return;
          }
          pressOnRoom = false;
          d.sitting = false;
          d.sitDy = 0;
          d.falling = false;
          d.walking = true;
          d.facing = nativeX >= d.x ? 1 : -1;
          target = clampDormX(nativeX);
          const near = nearestSit(target, 20);
          if (near) target = near.x;
          window.clearTimeout(bubbleTimer);
          const next = dormBubbles[Math.floor(Math.random() * dormBubbles.length)] ?? "(._.)";
          setBubble(next);
          bubbleTimer = window.setTimeout(() => setBubble(null), 2200);
        };

        app.view.style.cursor = "pointer";
        app.view.addEventListener("pointerdown", onPointerDown);
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
        cleanups.push(() => {
          app?.view.removeEventListener("pointerdown", onPointerDown);
          window.removeEventListener("pointermove", onPointerMove);
          window.removeEventListener("pointerup", onPointerUp);
        });
        if (!abort()) setFailed(null);
      } catch (err) {
        console.warn("Dorm viewer failed", err);
        if (!abort()) setFailed(err instanceof Error ? err.message : String(err));
      }
    }

    startTimer = window.setTimeout(() => {
      void boot();
    }, 0);
    return () => {
      destroyed = true;
      window.clearTimeout(startTimer);
      window.clearTimeout(bubbleTimer);
      for (const fn of cleanups) fn();
      sprite.current = null;
      app?.destroy(true);
    };
  }, [packId, roomH, roomW, scale, groundY, chibiScale, reduced, mobile, layoutKey]);

  return (
    <SectionShell id="dorm" kicker="Dormitory · Griffin lounge" title="Elegance Off-Duty">
      <p className={styles.lead}>
        A quiet library in GnK Dorms. Interactable.
      </p>
      <div className={styles.controls}>
        {dormPacks.map((pack) => (
          <button
            key={pack.id}
            type="button"
            className={styles.tab}
            aria-selected={packId === pack.id}
            onClick={() => setPackId(pack.id)}
          >
            {costumeLabels[pack.id]}
          </button>
        ))}
        <button
          type="button"
          className={styles.tab}
          aria-pressed={wander}
          onClick={() => {
            doll.current.sitting = false;
            setWander((v) => !v);
          }}
        >
          Wander {wander ? "on" : "off"}
        </button>
      </div>
      <div className={styles.viewport} ref={viewport}>
        <div className={styles.plate}>LV.90 · Mondragón</div>
        <div className={styles.scene} style={{ width: roomW, height: roomH }}>
          <div className={styles.host} ref={host} style={{ width: roomW, height: roomH }} />
          <div className={styles.bubble} ref={bubbleEl} hidden={!bubble}>
            {bubble}
          </div>
        </div>
        {failed ? <p className={styles.ph}>Dorm sprites did not load. {failed}</p> : null}
      </div>
    </SectionShell>
  );
}
